import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { AvailableFilterField } from "@/hooks/use-available-filter-fields";
import { cn } from "@/lib/utils";
import { FieldSelectItem } from "./field-select-item";

export interface FieldSelectProps {
  fields: AvailableFilterField[];
  value: string; // field id
  onChange: (fieldId: string) => void;
  className?: string;
}

export function FieldSelect({
  fields,
  value,
  onChange,
  className,
}: FieldSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedField = fields.find((f) => f.id === value);

  const filtered = useMemo(() => {
    if (!search) return fields;
    return fields.filter((f) =>
      f.label.toLowerCase().includes(search.toLowerCase()),
    );
  }, [fields, search]);

  const groupedFields = useMemo(() => {
    const groups: Record<string, AvailableFilterField[]> = {
      core: [],
      custom: [],
    };

    filtered.forEach((field) => {
      if (field.isCustom) {
        groups.custom.push(field);
      } else {
        groups.core.push(field);
      }
    });
    return groups;
  }, [filtered]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn("w-48 justify-between", className)}
          size="sm"
        >
          <span className="truncate">
            {selectedField ? selectedField.label : "Select field"}
          </span>
          <Search className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <div className="relative mb-2">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search fields..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 pr-2"
          />
        </div>
        <ScrollArea className="h-60 pr-1" onWheel={(e) => e.stopPropagation()}>
          {filtered.length === 0 ? (
            <div className="p-2 text-sm text-muted-foreground">No fields</div>
          ) : (
            <>
              {/* Core Fields Section */}
              {groupedFields.core.length > 0 && (
                <div className="mb-3">
                  <div className="px-2 py-1">
                    <h4 className="text-xs font-semibold text-muted-foreground">
                      Standard Fields
                    </h4>
                  </div>
                  <div className="space-y-1">
                    {groupedFields.core.map((field) => (
                      <FieldSelectItem
                        key={field.id}
                        field={field}
                        isSelected={field.id === value}
                        onSelect={() => {
                          onChange(field.id);
                          setOpen(false);
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Fields Section */}
              {groupedFields.custom.length > 0 && (
                <div className="mb-3">
                  <div className="px-2 py-1">
                    <h4 className="text-xs font-semibold text-muted-foreground">
                      Custom Fields
                    </h4>
                  </div>
                  <div className="space-y-1">
                    {groupedFields.custom.map((field) => (
                      <FieldSelectItem
                        key={field.id}
                        field={field}
                        isSelected={field.id === value}
                        onSelect={() => {
                          onChange(field.id);
                          setOpen(false);
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
